import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { vendorDocumentsService, getErrorMessage } from '../../services/apiService';

const VendorDocumentsPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing link');
      setLoading(false);
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await vendorDocumentsService.getByToken(token);
      if (!response.success) throw new Error('Failed to load');
      setData(response.data);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to load documents. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (docId) => {
    const url = vendorDocumentsService.previewUrl(token, docId);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = async (docId, filename) => {
    try {
      setDownloading(docId);
      await vendorDocumentsService.download(token, docId, filename);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to download document.');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50 p-4 overflow-y-auto">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <p className="text-sm text-slate-500">
              Please contact the administrator if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { vendor, documents } = data;

  return (
    <div className="h-screen overflow-y-auto bg-slate-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Vendor Documents</h1>

        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Vendor Information</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-slate-500">Company Name</dt>
                <dd className="font-medium text-slate-900">{vendor.company_name || '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Contact Person</dt>
                <dd className="font-medium text-slate-900">{vendor.contact_person_name || '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Email</dt>
                <dd className="font-medium text-slate-900">{vendor.email || '—'}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Documents & Agreements</h2>
            {documents.length === 0 ? (
              <p className="text-slate-600">No documents available.</p>
            ) : (
              <ul className="space-y-3">
                {documents.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex items-center justify-between gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">
                        {doc.type_label || doc.file_name}
                      </p>
                      <p className="text-sm text-slate-500 truncate">{doc.file_name}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreview(doc.id)}
                        className="flex items-center gap-1"
                      >
                        <Icon name="Eye" size={16} />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(doc.id, doc.file_name)}
                        disabled={downloading === doc.id}
                        className="flex items-center gap-1"
                      >
                        {downloading === doc.id ? (
                          <span className="animate-spin">⏳</span>
                        ) : (
                          <Icon name="Download" size={16} />
                        )}
                        Download
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VendorDocumentsPage;
